import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { libraryApi } from "@/lib/api";
import { openWixStore } from "@/lib/wixLinks";
import Loading from "@/components/app/Loading";
import { IDS } from "@/constants/testIds";
import { ArrowLeft, Download, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Reader() {
  const { itemId } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [lockInfo, setLockInfo] = useState(null);

  useEffect(() => {
    libraryApi.getItem(itemId)
      .then(setItem)
      .catch((e) => {
        if (e.status === 403 && e.detail?.product_id) {
          setLocked(true);
          setLockInfo(e.detail);
        } else {
          toast.error(e.message || "Failed to load item");
        }
      })
      .finally(() => setLoading(false));
  }, [itemId]);

  if (loading) return <Loading />;

  if (locked && lockInfo) {
    return (
      <div className="pa-card p-6 text-center max-w-lg mx-auto" data-testid={IDS.readerPage}>
        <Lock size={22} color="var(--pa-gold)" className="mx-auto mb-3" />
        <h2 className="pa-serif text-xl mb-2" style={{ color: "var(--pa-text-primary)" }}>{lockInfo.title}</h2>
        <p className="text-sm mb-4" style={{ color: "var(--pa-text-secondary)" }}>{lockInfo.message}</p>
        <button className="pa-btn-gold" onClick={openWixStore}>
          Unlock ${(lockInfo.price_cents / 100).toFixed(2)}
        </button>
      </div>
    );
  }

  if (!item) return null;

  const pdfUrl = item.has_pdf ? libraryApi.fileUrl(item.item_id, item.pdf_token) : null;
  const downloadUrl = item.has_pdf ? libraryApi.fileUrl(item.item_id, item.pdf_token, true) : null;

  return (
    <div className="space-y-4" data-testid={IDS.readerPage}>
      <div className="flex items-center justify-between gap-3">
        <button className="pa-btn-ghost inline-flex items-center gap-2" onClick={() => nav("/library")}>
          <ArrowLeft size={16} /> Library
        </button>
        {downloadUrl && (
          <a href={downloadUrl} className="pa-btn-outline inline-flex items-center gap-2 text-sm" download>
            <Download size={16} /> Download
          </a>
        )}
      </div>

      <header>
        <h1 className="pa-serif text-2xl md:text-3xl" style={{ color: "var(--pa-text-primary)" }}>{item.title}</h1>
        {item.summary && <p className="text-sm mt-1" style={{ color: "var(--pa-text-secondary)" }}>{item.summary}</p>}
      </header>

      {pdfUrl ? (
        <div
          className="w-full rounded-lg overflow-hidden"
          style={{ border: "1px solid var(--pa-card-border)", background: "var(--pa-card)", height: "calc(100vh - 220px)", minHeight: 480 }}
        >
          <iframe
            src={pdfUrl}
            title={item.title}
            className="w-full h-full"
            style={{ border: 0, background: "#fff" }}
          />
        </div>
      ) : item.sections?.length > 0 ? (
        <div className="space-y-6">
          {item.sections.map((s, i) => (
            <section key={i} className="pa-card p-5">
              {(s.heading || s.title) && (
                <h2 className="pa-serif text-lg mb-2" style={{ color: "var(--pa-gold)" }}>{s.heading || s.title}</h2>
              )}
              {s.body && <p className="text-sm whitespace-pre-line" style={{ color: "var(--pa-text-primary)" }}>{s.body}</p>}
            </section>
          ))}
        </div>
      ) : item.chapters?.length > 0 ? (
        <div className="space-y-6">
          {item.chapters.map((ch, i) => (
            <section key={i} className="pa-card p-5">
              {ch.title && (
                <h2 className="pa-serif text-lg mb-2" style={{ color: "var(--pa-gold)" }}>{ch.title}</h2>
              )}
              {ch.summary && <p className="text-xs mb-2 italic" style={{ color: "var(--pa-text-secondary)" }}>{ch.summary}</p>}
              {ch.body && <p className="text-sm whitespace-pre-line" style={{ color: "var(--pa-text-primary)" }}>{ch.body}</p>}
            </section>
          ))}
        </div>
      ) : (
        <div className="pa-card p-6 text-sm" style={{ color: "var(--pa-text-secondary)" }}>
          No content available for this item.
        </div>
      )}
    </div>
  );
}
